import type {
  FootprintResponse,
  IShopSystemStrategy,
} from "../IShopSystemStrategy";
import type {
  ShopConfig,
  AppEntry,
} from "../../../../../pina-server/node_modules/@prisma/client";
import type { ShopifyAjaxCart, ShopifyAjaxCartItem } from "./types";

export class ShopifyShopSystemStrategy implements IShopSystemStrategy {
  async loadingDone(): Promise<void> {}
  registerEventHandlers(): void {}
  async refreshCart(): Promise<void> {}
  async localeAwareFetch(input: string, init?: RequestInit): Promise<Response> {
    // See here: https://shopify.dev/docs/api/ajax#locale-aware-urls
    const RETRY_COUNT = 3;
    const RETRY_DELAY = 200;

    for (let i = 0; i < RETRY_COUNT; i++) {
      try {
        //@ts-ignore
        let endpoint = (window.Shopify.routes ?? { root: "/" }).root + input;
        const proxy = window.sessionStorage.getItem("proxy");
        if (proxy) endpoint += `?proxy=${proxy}`;
        // Await here to throw on fetch errors.
        const shortCircuitRequest = new Promise<Response>(
          async (resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error("Timeout"));
            }, 1500);
            const result = await fetch(endpoint, init);
            clearTimeout(timeout);
            resolve(result);
          },
        );
        return await shortCircuitRequest;
      } catch (e) {
        console.error(e);
        console.error("Retrying fetch...");
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      }
    }

    throw new Error("Failed to fetch after retries.");
  }

  async getCart(): Promise<ShopifyAjaxCart | undefined> {
    const response = await this.localeAwareFetch("cart.js");
    if (!response.ok) {
      return undefined;
    }
    return await response.json();
  }

  getOffsetItem(cart?: ShopifyAjaxCart): ShopifyAjaxCartItem | undefined {
    // TODO: Improve detection
    return cart?.items.find(
      (item) =>
        item.title === null ||
        item.title.startsWith("Pina Earth - Klimabeitrag"),
    );
  }

  async getConfig(
    page: string,
  ): Promise<(ShopConfig & { appEntries: AppEntry[] }) | null> {
    const request = await this.localeAwareFetch(
      `apps/pina/api/proxy/shop/$id/config/${page}`,
    );

    if (!request.ok) {
      return null;
    }

    const config = await request.json();
    return config;
  }

  async getFootprint(): Promise<FootprintResponse | undefined> {
    const cart = await this.getCart();

    const response = await this.localeAwareFetch(
      "apps/pina/api/footprint/calculate",
      {
        method: "POST",
        body: JSON.stringify(cart),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      return undefined;
    }

    const offset = await response.json();

    offset.inCart = this.getOffsetItem(cart) !== undefined;
    offset.orderValueWithoutOffset = cart?.total_price;

    return offset;
  }

  beginOffset() {}

  endOffset() {}

  bundledSections(): string[] {
    return [];
  }

  bundledSectionsCallback(sections: any) {
    return;
  }

  async toggleOffset(
    state: boolean,
    refreshCart: boolean = true,
  ): Promise<boolean> {
    const cart = await this.getCart();
    const offsetItem = this.getOffsetItem(cart);

    this.beginOffset();

    if (state) {
      if (offsetItem || cart?.items.length === 0) {
        this.endOffset();
        return false;
      }
      const footprint = await this.getFootprint();
      if (!footprint) {
        this.endOffset();
        return false;
      }
      const productResponse = await this.localeAwareFetch(
        "apps/pina/api/footprint/product",
        {
          method: "POST",
          body: JSON.stringify(cart),
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      const variantId = await productResponse.json();

      if (!variantId) {
        this.endOffset();
        return false;
      }

      const response = await this.localeAwareFetch("cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              // Is in the form of "gid://shopify/ProductVariant/1234567890"
              id: variantId.split("/").pop(),
              quantity: 1,
            },
          ],
          sections: this.bundledSections(),
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        this.endOffset();
        return false;
      }

      const data = await response.json();

      if (refreshCart) {
        this.refreshCart();
        this.bundledSectionsCallback(data.sections);
      }

      this.endOffset();
      return true;
    } else {
      if (!offsetItem) {
        this.endOffset();
        return false;
      }
      const response = await this.localeAwareFetch("cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: offsetItem?.id?.toString(),
          quantity: 0,
          sections: this.bundledSections(),
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        this.endOffset();
        return false;
      }

      const data = await response.json();

      if (refreshCart) {
        this.refreshCart();
        this.bundledSectionsCallback(data.sections);
      }

      this.endOffset();
      return true;
    }
  }
}
